import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const EMPTY = {
  name: "", category: "", area: "", building: "", floor: "", room: "", manufacturer: "", model: "",
  serial_number: "", inventory_number: "", department: "", responsible: "", service_provider: "", contact: "", notes: "",
};

function Field({ label, children }) {
  return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;
}

export default function AssetForm({ open, onClose, onSaved, initial, categories }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = !!initial;

  useEffect(() => { if (open) setForm(initial ? { ...EMPTY, ...initial } : EMPTY); }, [open, initial]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name ist erforderlich");
    setSaving(true);
    try {
      if (editing) await api.put(`/inspections/assets/${initial.id}`, form);
      else await api.post("/inspections/assets", form);
      toast.success(editing ? "Anlage aktualisiert" : "Anlage angelegt");
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head text-xl">{editing ? "Anlage / Gerät bearbeiten" : "Neue Anlage / Gerät"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1" data-testid="asset-form">
          <Field label="Bezeichnung *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="asset-name" placeholder="z. B. Patientenlifter Modell XY" /></Field>
          <Field label="Kategorie / Fachbereich">
            <Select value={form.category || "__none"} onValueChange={(v) => set("category", v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="asset-category"><SelectValue placeholder="Kategorie wählen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— keine —</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Bereich"><Input value={form.area} onChange={(e) => set("area", e.target.value)} /></Field>
            <Field label="Gebäude"><Input value={form.building} onChange={(e) => set("building", e.target.value)} /></Field>
            <Field label="Etage"><Input value={form.floor} onChange={(e) => set("floor", e.target.value)} /></Field>
            <Field label="Raum"><Input value={form.room} onChange={(e) => set("room", e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Hersteller"><Input value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} /></Field>
            <Field label="Modell"><Input value={form.model} onChange={(e) => set("model", e.target.value)} /></Field>
            <Field label="Seriennr."><Input value={form.serial_number} onChange={(e) => set("serial_number", e.target.value)} /></Field>
            <Field label="Inventarnr."><Input value={form.inventory_number} onChange={(e) => set("inventory_number", e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Abteilung"><Input value={form.department} onChange={(e) => set("department", e.target.value)} /></Field>
            <Field label="Verantwortlicher"><Input value={form.responsible} onChange={(e) => set("responsible", e.target.value)} /></Field>
            <Field label="Dienstleister"><Input value={form.service_provider} onChange={(e) => set("service_provider", e.target.value)} /></Field>
            <Field label="Ansprechpartner"><Input value={form.contact} onChange={(e) => set("contact", e.target.value)} /></Field>
          </div>
          <Field label="Bemerkungen"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" disabled={saving} data-testid="save-asset-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Speichern" : "Anlegen")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
